import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { AuthToken } from '../models/auth';

/**
 * In-memory home for the bearer access token. The token deliberately lives outside the NgRx
 * store so it cannot ride along with state-tree pumps — metareducers, redux devtools, SSR
 * state transfer, "save my workspace" features — into anywhere persistent or observable.
 *
 * The non-secret identity bits (isAuthenticated, username, userId, roles) still live in the
 * `auth` slice; only the bearer itself is held here. On a hard reload the holder starts empty
 * and {@code AuthRefreshService.bootstrap} refills it from the httpOnly refresh cookie.
 */
@Injectable({ providedIn: 'root' })
export class AccessTokenHolder {
  private readonly subject = new BehaviorSubject<AuthToken | null>(null);

  /** Push the current token to subscribers (e.g. the STOMP client) when it rotates. */
  readonly token$: Observable<AuthToken | null> = this.subject.asObservable();

  get(): AuthToken | null {
    return this.subject.getValue();
  }

  set(token: AuthToken): void {
    this.subject.next(token);
  }

  clear(): void {
    this.subject.next(null);
  }
}
